import { makeAutoObservable, reaction, runInAction, type IReactionDisposer } from 'mobx'
import { nanoid } from 'nanoid'
import { layoutStore } from 'shared/layout'
import { type IContactsDetailsVariant } from './types'

const blockTypes = [
  'notes',
  'tags',
  'integration',
  'contactDetails',
  'conversation',
  'contactDetailsAllFields',
  'callInsightIntegration',
  'callInsightContactDetails',
  'callInsightAllFields',
  'quickLinks',
  'contactsGroup',
] as const

export type IBlocksTypes = (typeof blockTypes)[number]

const openStatusStoreKey = 'contactDetailsOpenStatus'
type IOpenStatusStoreType = {
  [key in IBlocksTypes]?: boolean
}

export class ContactsDetailsGlobalStore {
  private _openStatus: Map<IBlocksTypes, boolean> = new Map(
    blockTypes.map((item) => {
      if (['contactDetails'].includes(item)) return [item, true]

      return [item, false]
    })
  )
  private _collapse = true
  private _variant: IContactsDetailsVariant = 'conversation'
  private _openStatusTrigger = ''
  private _overlay = false
  private _disposeOpenStatus: IReactionDisposer | null = null

  constructor() {
    makeAutoObservable(this)

    this.reactionOpenStatus()
    this.checkStoredOpenStatus()
  }

  dispose = () => {
    this._collapse = true
    this._overlay = false
    this._openStatusTrigger = ''
    this._disposeOpenStatus?.()
  }

  isOpenBlock = (blockType: IBlocksTypes) => {
    return this._openStatus.get(blockType)
  }

  toggleBlockView = (blockType: IBlocksTypes) => {
    const status = this._openStatus.get(blockType)
    this._openStatus.set(blockType, !status)
    this._openStatusTrigger = nanoid()
  }

  openBlockView = (blockType: IBlocksTypes) => {
    this._openStatus.set(blockType, true)
    this._openStatusTrigger = nanoid()
  }

  handleDocumentElementBlur = () => {
    const activeElement = document.activeElement as HTMLElement
    if (activeElement) activeElement.blur()
  }

  handleToggleCollapse = () => {
    this.handleDocumentElementBlur()
    this._collapse = !this._collapse
  }

  handleChangeCollapse = (status: boolean) => {
    this.handleDocumentElementBlur()
    this._collapse = status
    this._overlay = false
  }

  handleSetVariant = (type: IContactsDetailsVariant) => {
    runInAction(() => {
      this._variant = type
    })
  }

  storeOpenStatus = () => {
    const data: IOpenStatusStoreType = {}

    this.openStatusList.forEach(([key, status]) => {
      data[key] = status
    })

    localStorage.setItem(openStatusStoreKey, JSON.stringify(data))
  }

  checkStoredOpenStatus = () => {
    const dataJSON = localStorage.getItem(openStatusStoreKey)
    if (!dataJSON) return

    const data = JSON.parse(dataJSON) as IOpenStatusStoreType
    blockTypes.forEach((key) => {
      if (data[key]) {
        this._openStatus.set(key, !!data[key])
      }
    })
  }

  get isShowDetailsOverlayConversation() {
    if (this._overlay) return true

    const { isMobileViewWithoutMSize } = layoutStore

    return isMobileViewWithoutMSize && !this.isCollapse
  }

  get isShowDetailsOverlayContacts() {
    if (this._overlay) return true

    const { isMediumView } = layoutStore

    return isMediumView && !this.isCollapse
  }

  get isShowContactDetailsOverlay() {
    return this._variant === 'contacts'
      ? this.isShowDetailsOverlayContacts
      : this.isShowDetailsOverlayConversation
  }

  get isCollapse() {
    return this._collapse
  }

  get openStatusList() {
    return Array.from(this._openStatus.entries())
  }

  reactionOpenStatus() {
    this._disposeOpenStatus?.()
    this._disposeOpenStatus = reaction(
      () => this._openStatusTrigger,
      () => this.storeOpenStatus()
    )
  }

  setOverlay = (value: boolean) => {
    this._overlay = value
  }
}

export const contactsDetailsGlobalStore = new ContactsDetailsGlobalStore()
